/**
 * FormModal Component
 *
 * Reusable form dialog built on top of {@link Modal}.
 * Provides a standardized layout with title, optional description,
 * scrollable form content area, error display, and cancel / submit buttons.
 *
 * Eliminates imperative `requestSubmit()` calls by generating a stable
 * `useId()`-based form id and linking the external submit button via the
 * HTML `form` attribute (`<button type="submit" form={formId} />`).
 *
 * @example
 * ```tsx
 * <FormModal
 *   open={isOpen}
 *   title="Edit user"
 *   description="Update user details."
 *   submitLabel="Save"
 *   cancelLabel="Cancel"
 *   loading={isSaving}
 *   error={errorMessage}
 *   onSubmit={handleSubmit}
 *   onClose={() => setIsOpen(false)}
 * >
 *   <InputWrapper label="Name" htmlFor="name">
 *     <Input id="name" value={name} onChange={e => setName(e.target.value)} />
 *   </InputWrapper>
 * </FormModal>
 * ```
 */
import * as React from 'react';
import { getTestId, type TestIdProps } from '../utils';
import { Modal, type ModalProps } from './Modal';
import { Button } from './Button';

export interface FormModalProps extends TestIdProps {
  /** Whether the modal is visible */
  open: boolean;
  /** Modal title */
  title: string;
  /** Optional description displayed below the title */
  description?: React.ReactNode;
  /** Label for the submit button */
  submitLabel: string;
  /** Label for the cancel button */
  cancelLabel: string;
  /** Whether the form submission is in progress (disables both buttons) */
  loading?: boolean;
  /** Extra condition to disable the submit button (independent of `loading`) */
  submitDisabled?: boolean;
  /** Look of the submit button; `error` for a destructive submit such as a revocation */
  submitVariant?: 'primary' | 'error';
  /** Error message displayed below the form fields */
  error?: string | null;
  /** Called when the form is submitted (after internal `preventDefault`) */
  onSubmit: () => void | Promise<void>;
  /** Called when the modal is dismissed (cancel button or backdrop click) */
  onClose: () => void;
  /** Modal size — defaults to `'md'` */
  size?: ModalProps['size'];
  /** Additional CSS classes for the modal wrapper */
  className?: string;
  /** Form field content */
  children: React.ReactNode;
}

export const FormModal: React.FC<FormModalProps> = ({
  open,
  title,
  description,
  submitLabel,
  cancelLabel,
  loading = false,
  submitDisabled = false,
  submitVariant = 'primary',
  error,
  onSubmit,
  onClose,
  size = 'md',
  className,
  children,
  testId,
  id
}) => {
  const formId = React.useId();
  const [asyncError, setAsyncError] = React.useState<string | null>(null);

  const handleFormSubmit = (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    setAsyncError(null);
    const result = onSubmit();
    if (result instanceof Promise) {
      result.catch((err: unknown) => {
        const message = err instanceof Error ? err.message : String(err);
        setAsyncError(message);
      });
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size={size}
      className={className}
      testId={getTestId({ testId, id })}
    >
      <Modal.Header onClose={onClose} label={title} description={description} />
      <Modal.Content className='w-full h-auto!'>
        <form id={formId} onSubmit={handleFormSubmit} className='space-y-md w-full px-xs'>
          {children}
          {(error || asyncError) && (
            <p className='text-sm text-error-text'>{error ?? asyncError}</p>
          )}
        </form>
      </Modal.Content>
      <Modal.Footer>
        <Modal.Action>
          <Button variant='default' onClick={onClose} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            type='submit'
            form={formId}
            variant={submitVariant}
            disabled={loading || submitDisabled}
          >
            {submitLabel}
          </Button>
        </Modal.Action>
      </Modal.Footer>
    </Modal>
  );
};

FormModal.displayName = 'FormModal';
